(() => {
  // Checkout FAB for cart drawer
  // - Shows only when checkout CTA is out of view
  // - Scrolls the drawer to checkout when tapped
  // NOTE: intentionally avoids global MutationObservers to prevent UI regressions.

  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

  function qs(root, sel) {
    return (root || document).querySelector(sel);
  }

  function qsa(root, sel) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function getDrawer() {
    return qs(document, "cart-drawer-component") || qs(document, "[data-cart-drawer]");
  }

  function getDialog(drawer) {
    if (!drawer) return null;
    return qs(drawer, ".cart-drawer__dialog") || qs(drawer, "dialog") || drawer;
  }

  function getCheckoutButton(scope) {
    if (!scope) return null;
    return (
      qs(scope, 'a[href*="/checkout"]') ||
      qs(scope, 'button[name="checkout"]') ||
      qs(scope, "[data-checkout]") ||
      null
    );
  }

  function isTransparent(color) {
    if (!color) return true;
    const c = String(color).trim().toLowerCase();
    return c === "transparent" || c === "rgba(0, 0, 0, 0)" || /,\s*0\)$/.test(c);
  }

  function applyCheckoutColors(fab, checkoutBtn) {
    if (!fab || !checkoutBtn) return;

    const cs = window.getComputedStyle(checkoutBtn);
    let bg = cs.backgroundColor;
    let fg = cs.color;

    const root = window.getComputedStyle(document.documentElement);

    if (isTransparent(bg)) {
      bg =
        (root.getPropertyValue("--button-background") || "").trim() ||
        (root.getPropertyValue("--color-button") || "").trim() ||
        bg;
    }

    if (!fg || isTransparent(fg)) {
      fg =
        (root.getPropertyValue("--button-text") || "").trim() ||
        (root.getPropertyValue("--color-button-text") || "").trim() ||
        fg;
    }

    if (bg && !isTransparent(bg)) fab.style.backgroundColor = bg;
    if (fg && !isTransparent(fg)) fab.style.color = fg;
  }

  function findScrollableAncestor(startEl, boundaryEl) {
    let node = startEl ? startEl.parentElement : null;
    while (node && node !== boundaryEl && node !== document.body) {
      const cs = window.getComputedStyle(node);
      const oy = cs.overflowY;
      if ((oy === "auto" || oy === "scroll") && node.scrollHeight > node.clientHeight + 10) {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  }

  function findScrollableInside(rootEl) {
    if (!rootEl) return null;
    const all = rootEl.querySelectorAll("*");
    for (let i = 0; i < all.length; i++) {
      const el = all[i];
      const cs = window.getComputedStyle(el);
      const oy = cs.overflowY;
      if ((oy === "auto" || oy === "scroll") && el.scrollHeight > el.clientHeight + 10) {
        return el;
      }
    }
    return null;
  }

  function isVisibleInScroller(el, scroller) {
    if (!el || !scroller) return false;
    const elRect = el.getBoundingClientRect();
    const scRect = scroller.getBoundingClientRect();
    const visiblePx = Math.min(elRect.bottom, scRect.bottom) - Math.max(elRect.top, scRect.top);
    const ratio = visiblePx / Math.max(1, elRect.height);
    return ratio >= 0.4;
  }

  function scrollToCheckout(checkoutBtn, scroller) {
    if (!checkoutBtn || !scroller) return;

    const scRect = scroller.getBoundingClientRect();
    const targetRect = checkoutBtn.getBoundingClientRect();

    const currentTop = scroller.scrollTop;
    const delta = targetRect.top - scRect.top;
    const targetTop = currentTop + delta - 24;

    scroller.scrollTo({
      top: clamp(targetTop, 0, scroller.scrollHeight),
      behavior: "smooth",
    });
  }

  function initForFab(fab) {
    if (!fab || fab.getAttribute("data-fab-inited") === "1") return;
    fab.setAttribute("data-fab-inited", "1");

    let raf = null;
    let boundScroller = null;

    function update() {
      raf = null;

      const drawer = getDrawer();
      const dialog = getDialog(drawer) || document;

      const checkoutBtn = getCheckoutButton(dialog);
      if (!checkoutBtn) {
        fab.classList.remove("is-visible");
        return;
      }

      applyCheckoutColors(fab, checkoutBtn);

      const scroller =
        findScrollableAncestor(checkoutBtn, dialog) ||
        findScrollableInside(dialog);

      if (!scroller) {
        fab.classList.remove("is-visible");
        return;
      }

      if (boundScroller !== scroller) {
        if (boundScroller) boundScroller.removeEventListener("scroll", scheduleUpdate);
        boundScroller = scroller;
        boundScroller.addEventListener("scroll", scheduleUpdate, { passive: true });
      }

      const visible = isVisibleInScroller(checkoutBtn, scroller);
      fab.classList.toggle("is-visible", !visible);

      fab._target = checkoutBtn;
      fab._scroller = scroller;
    }

    function scheduleUpdate() {
      if (raf) return;
      raf = requestAnimationFrame(update);
    }

    fab.addEventListener("click", function () {
      const drawer = getDrawer();
      const dialog = getDialog(drawer) || document;
      const target = fab._target || getCheckoutButton(dialog);
      const scroller =
        fab._scroller ||
        (target ? (findScrollableAncestor(target, dialog) || findScrollableInside(dialog)) : null);

      if (!target || !scroller) return;
      scrollToCheckout(target, scroller);
    });

    document.addEventListener("cart:update", scheduleUpdate);
    document.addEventListener("cart:refresh", scheduleUpdate);
    window.addEventListener("resize", scheduleUpdate);

    // initial burst (drawer layout can settle after open)
    update();
    setTimeout(update, 50);
    setTimeout(update, 150);
  }

  function boot() {
    const drawer = getDrawer();
    if (!drawer) return;
    const dialog = getDialog(drawer) || drawer;
    qsa(dialog, "[data-checkout-fab]").forEach(initForFab);
  }

  // Boot now
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }

  // Re-boot when drawer opens (patch open) or when sections reload (theme editor)
  document.addEventListener("shopify:section:load", boot);

  if (window.customElements && customElements.get("cart-drawer-component")) {
    const proto = customElements.get("cart-drawer-component").prototype;
    if (proto && !proto.__fabPatchedStable) {
      proto.__fabPatchedStable = true;
      const originalOpen = proto.open;
      if (typeof originalOpen === "function") {
        proto.open = function () {
          const out = originalOpen.apply(this, arguments);
          setTimeout(boot, 0);
          setTimeout(boot, 60);
          setTimeout(boot, 160);
          return out;
        };
      }
    }
  } else if (window.customElements && window.customElements.whenDefined) {
    window.customElements.whenDefined("cart-drawer-component").then(() => {
      // try again once defined
      try {
        const proto = customElements.get("cart-drawer-component").prototype;
        if (proto && !proto.__fabPatchedStable) {
          proto.__fabPatchedStable = true;
          const originalOpen = proto.open;
          if (typeof originalOpen === "function") {
            proto.open = function () {
              const out = originalOpen.apply(this, arguments);
              setTimeout(boot, 0);
              setTimeout(boot, 60);
              setTimeout(boot, 160);
              return out;
            };
          }
        }
      } catch (e) {}
    });
  }
})();